import { useEffect, useState } from "react";
import { AdminLayout } from "@/pages/Admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { UserPlus, Shield, Users2 } from "lucide-react";

const STAFF_ROLES = [
  { value: "admin", label: "Platform Owner", desc: "Full access to every module" },
  { value: "sales_team", label: "Sales Team", desc: "Investor CRM + KYC approvals" },
  { value: "property_uploader", label: "Property Uploader", desc: "Create & edit properties" },
  { value: "accounts", label: "Accounts", desc: "Orders, payouts, commissions, withdrawals" },
];

const ROLE_LABELS = Object.fromEntries(STAFF_ROLES.map((r) => [r.value, r.label]));

export function AdminTeam() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales_team" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Staff account created");
      setForm({ name: "", email: "", password: "", role: "sales_team" });
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const changeRole = async (uid, newRole) => {
    try {
      await api.patch(`/admin/users/${uid}/role`, { role: newRole });
      toast.success(`Role updated to ${ROLE_LABELS[newRole] || newRole}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout active="team">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="overline text-[#C5A059]">Access Control</div>
          <h1 className="font-serif text-4xl mt-2 flex items-center gap-2">
            <Shield className="h-8 w-8 text-[#C5A059]" />Team & Roles
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-gold rounded-full" data-testid="admin-invite-staff">
              <UserPlus className="h-4 w-4 mr-2" />Invite staff
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md text-slate-900">
            <DialogHeader><DialogTitle>Create staff account</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="staff-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="staff-email" /></div>
              <div><Label>Temporary password</Label><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="staff-password" /></div>
              <div>
                <Label>Role</Label>
                <select
                  className="w-full h-10 border border-input rounded-md px-3 text-sm bg-transparent"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="staff-role"
                >
                  {STAFF_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <Button type="submit" className="btn-gold rounded-full w-full" disabled={busy} data-testid="staff-create-submit">
                {busy ? "Creating…" : "Create account"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-4 gap-3 mb-8">
        {STAFF_ROLES.map((r) => {
          const count = items.filter((u) => u.role === r.value).length;
          return (
            <Card key={r.value} className="bg-[#111827] border-white/5 text-slate-100">
              <CardContent className="pt-5">
                <div className="overline text-[#C5A059]">{r.label}</div>
                <div className="metric-value text-3xl mt-2">{count}</div>
                <div className="text-xs text-white/60 mt-2">{r.desc}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr>
            <th className="p-3 text-left">Name</th>
            <th className="p-3 text-left">Email</th>
            <th className="p-3 text-center">Role</th>
            <th className="p-3 text-left">Joined</th>
            <th className="p-3 text-right">Change role</th>
          </tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5" data-testid={`staff-row-${u.user_id}`}>
                <td className="p-3">{u.name}</td>
                <td className="p-3 text-white/60">{u.email}</td>
                <td className="p-3 text-center">
                  <Badge className="bg-[#C5A059]/20 text-[#C5A059] hover:bg-[#C5A059]/20">{ROLE_LABELS[u.role] || u.role}</Badge>
                </td>
                <td className="p-3 text-white/60">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right">
                  <select
                    className="bg-transparent border border-white/10 rounded-md h-8 px-2 text-xs text-white"
                    value={u.role}
                    onChange={(e) => changeRole(u.user_id, e.target.value)}
                    data-testid={`role-select-${u.user_id}`}
                  >
                    {STAFF_ROLES.map((r) => <option key={r.value} value={r.value} className="text-slate-900">{r.label}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="p-10 text-center text-white/50">No staff members yet. Invite your first teammate above.</td></tr>}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
